import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { dashboardAPI, attendanceAPI } from '@/lib/api';
import { KpiCard } from '@/components/KpiCard';
import { StatusPill } from '@/components/StatusPill';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, FileText, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import DailyOverviewPage from './DailyOverviewPage';

export default function DashboardPage() {
  const { user, isSuperUser, isManager } = useAuth();
  const isAdmin = isSuperUser || isManager;
  const [stats, setStats] = useState({});
  const [myStatus, setMyStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) return;
    loadData();
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const statsRes = await dashboardAPI.stats();
      setStats(statsRes.data);
      try {
        const statusRes = await attendanceAPI.myStatus();
        setMyStatus(statusRes.data);
      } catch (e) { console.error(e); }
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Super users and managers land directly on the consolidated Daily Overview
  if (isAdmin) return <DailyOverviewPage />;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-muted animate-pulse rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground mt-1">Here{'\u2019'}s your overview for today</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Days Present" value={stats.days_present || 0} subtitle="This month" icon={CheckCircle} />
        <KpiCard title="Lates" value={stats.monthly_lates || 0} subtitle="This month" icon={Clock} />
        <KpiCard title="Half Days" value={stats.monthly_half_days || 0} subtitle="This month" icon={AlertTriangle} />
        <KpiCard title="Pending Leaves" value={stats.pending_leaves || 0} icon={FileText} />
      </div>

      {myStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Today{'\u2019'}s Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <StatusPill status={myStatus.checked_in ? 'checked-in' : 'absent'} />
              {myStatus.attendance && (
                <div className="text-sm text-muted-foreground">
                  <span>Checked in at: </span>
                  <span className="font-medium text-foreground tabular-nums">
                    {myStatus.attendance.check_in_display || myStatus.attendance.check_in_time_local}
                  </span>
                  {myStatus.attendance.is_late && (
                    <span className="ml-2 text-amber-600">({myStatus.attendance.late_minutes} min late)</span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
